import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import {supabase} from '../../api/supabase';

// ============================================================================
// Types & Interfaces
// ============================================================================

interface VerificationPose {
  pose: string;
  instruction: string;
  icon: string;
}

interface VerificationRequest {
  id: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  submittedAt: string;
  rejectionReason?: string;
}

interface VerificationStatus {
  isVerified: boolean;
  verifiedAt?: string;
  currentRequest?: VerificationRequest;
  canSubmitNew: boolean;
  nextSubmitAt?: string;
}

interface VerificationState {
  currentPose: VerificationPose | null;
  status: VerificationStatus | null;
  loading: boolean;
  submitting: boolean;
  error: string | null;
}

const initialState: VerificationState = {
  currentPose: null,
  status: null,
  loading: false,
  submitting: false,
  error: null,
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate SHA-256 hash of a file for duplicate detection (browser crypto API)
 */
const calculatePhotoHash = async (file: File): Promise<string> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (error) {
    console.error('Error calculating photo hash:', error);
    throw new Error('Failed to calculate photo hash');
  }
};

/**
 * Upload verification photo (File object) to Supabase Storage
 */
const uploadVerificationPhoto = async (
  userId: string,
  photoFile: File,
): Promise<string | null> => {
  if (!userId || !photoFile) {
    return null;
  }

  try {
    // Get file extension
    const fileNameParts = photoFile.name.split('.');
    const extension =
      fileNameParts.length > 1
        ? fileNameParts[fileNameParts.length - 1]
        : 'jpg';

    // Create unique file path
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substring(2);
    const filePath = `${userId}/${timestamp}_${randomSuffix}_verification.${extension}`;

    // Upload file directly (web supports File objects)
    const { data, error } = await supabase.storage
      .from('verification-photos')
      .upload(filePath, photoFile, {
        contentType: photoFile.type || `image/${extension}`,
        upsert: false,
      });

    if (error) {
      console.error('Upload error:', error);
      return null;
    }

    // Get public URL
    const {
      data: {publicUrl},
    } = supabase.storage.from('verification-photos').getPublicUrl(filePath);

    return publicUrl;
  } catch (error) {
    console.error('Error uploading verification photo:', error);
    return null;
  }
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetch a random verification pose
 */
export const fetchVerificationPose = createAsyncThunk(
  'verification/fetchPose',
  async (_, {rejectWithValue}) => {
    try {
      const {data, error} = await supabase.rpc('get_random_verification_pose');

      if (error) {
        throw new Error(`Failed to fetch pose: ${error.message}`);
      }

      if (!data || data.length === 0) {
        throw new Error('No verification pose returned');
      }

      const poseData = Array.isArray(data) ? data[0] : data;

      return {
        pose: poseData.pose_type,
        instruction: poseData.instruction,
        icon: poseData.icon,
      } as VerificationPose;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch verification pose');
    }
  },
);

/**
 * Fetch verification status for a user
 */
export const fetchVerificationStatus = createAsyncThunk(
  'verification/fetchStatus',
  async (userId: string, {rejectWithValue}) => {
    try {
      const {data, error} = await supabase.rpc('get_verification_status', {
        p_user_id: userId,
      });

      if (error) {
        throw new Error(`Failed to fetch status: ${error.message}`);
      }

      if (!data || data.length === 0) {
        // No verification data yet - user hasn't verified
        return {
          isVerified: false,
          canSubmitNew: true,
        } as VerificationStatus;
      }

      const statusData = Array.isArray(data) ? data[0] : data;

      return {
        isVerified: statusData.is_verified || false,
        verifiedAt: statusData.verified_at,
        currentRequest: statusData.current_request_id
          ? {
              id: statusData.current_request_id,
              status: statusData.current_request_status,
              submittedAt: statusData.current_request_created_at,
              rejectionReason: statusData.current_request_rejection_reason,
            }
          : undefined,
        canSubmitNew: statusData.can_submit_new || false,
        nextSubmitAt: statusData.next_submit_at,
      } as VerificationStatus;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Failed to fetch verification status',
      );
    }
  },
);

/**
 * Submit verification request with photo
 */
export const submitVerification = createAsyncThunk(
  'verification/submit',
  async (
    params: {
      userId: string;
      poseType: string;
      photoFile: File;
      photoHash: string;
    },
    {rejectWithValue},
  ) => {
    try {
      const {userId, poseType, photoFile, photoHash} = params;

      // Upload photo to storage
      const photoUrl = await uploadVerificationPhoto(userId, photoFile);

      if (!photoUrl) {
        throw new Error('Failed to upload verification photo');
      }

      // Submit verification request
      const {data, error} = await supabase.rpc('submit_verification_request', {
        p_user_id: userId,
        p_pose_type: poseType,
        p_verification_photo_url: photoUrl,
        p_photo_hash: photoHash,
      });

      if (error) {
        throw new Error(`Failed to submit verification: ${error.message}`);
      }

      return data;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Failed to submit verification request',
      );
    }
  },
);

// ============================================================================
// Slice
// ============================================================================

const verificationSlice = createSlice({
  name: 'verification',
  initialState,
  reducers: {
    resetVerification: state => {
      state.currentPose = null;
      state.error = null;
    },
    clearError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    // Fetch pose
    builder.addCase(fetchVerificationPose.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchVerificationPose.fulfilled, (state, action) => {
      state.loading = false;
      state.currentPose = action.payload;
    });
    builder.addCase(fetchVerificationPose.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch status
    builder.addCase(fetchVerificationStatus.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchVerificationStatus.fulfilled, (state, action) => {
      state.loading = false;
      state.status = action.payload;
    });
    builder.addCase(fetchVerificationStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Submit verification
    builder.addCase(submitVerification.pending, state => {
      state.submitting = true;
      state.error = null;
    });
    builder.addCase(submitVerification.fulfilled, state => {
      state.submitting = false;
      // Status will be refreshed by calling fetchVerificationStatus again
    });
    builder.addCase(submitVerification.rejected, (state, action) => {
      state.submitting = false;
      state.error = action.payload as string;
    });
  },
});

export const {resetVerification, clearError} = verificationSlice.actions;
export default verificationSlice.reducer;

// Export helper function for use in components
export {calculatePhotoHash};
